import { DateRangePicker } from '@salecycle/ui-component-library';
import moment from 'moment-timezone';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'moment/locale/en-gb';
import { DATE_DISPLAY_FORMAT } from '../data/ReportableDataConsts';

interface ICustomDateRangeProps {
  dayRange?: number;
  endDate?: moment.Moment;
  startDate?: moment.Moment;
  changeStartDate?: (date: moment.Moment) => void;
  changeEndDate?: (date: moment.Moment) => void;
  isDisabled?: boolean;
  label?: string;
  onClose: (startDate: moment.Moment, endDate: moment.Moment) => void;
  startDateId?: string;
  endDateId?: string;
  /** Display the selected dates in the `D MMM 'YY` format */
  alternativeFormat?: boolean;
}

interface IDates {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

const CustomDateRange: FC<ICustomDateRangeProps> = ({
  dayRange,
  endDate,
  startDate,
  isDisabled,
  label,
  onClose,
  changeStartDate,
  changeEndDate,
  startDateId = 'DateRangePickerStart',
  endDateId = 'DateRangePickerEnd',
  alternativeFormat,
}) => {
  const { t } = useTranslation('dashboard');

  const [start, setStart] = useState<moment.Moment>(null);
  const [end, setEnd] = useState<moment.Moment>(null);
  const [focus, setFocus] = useState<string>();

  const handleStartDateChange = (date: moment.Moment) => {
    if (changeStartDate) {
      changeStartDate(date);
    } else {
      setStart(date);
    }
  };

  const handleEndDateChange = (date: moment.Moment) => {
    if (changeStartDate) {
      changeEndDate(date);
    } else {
      setEnd(date);
    }
  };

  const handleOnDatesChange = ({ startDate, endDate }: IDates) => {
    handleStartDateChange(startDate);
    handleEndDateChange(endDate);
  };

  return (
    <DateRangePicker
      label={`${label ? label : t('filter.customDateRange.label')}:`}
      startDate={startDate ?? start}
      startDateId={startDateId}
      endDate={endDate ?? end}
      endDateId={endDateId}
      focusedInput={focus}
      onFocusChange={(focusedInput: string) => setFocus(focusedInput)}
      onDatesChange={handleOnDatesChange}
      onClose={({ startDate, endDate }: IDates) => {
        if (startDate && endDate) {
          onClose(startDate, endDate);
        }
      }}
      isPastDatesOnly={true}
      minimumNights={0}
      spacingBelow={false}
      isMini={true}
      isBold={true}
      dayRange={dayRange}
      disabled={isDisabled}
      displayFormat={alternativeFormat ? DATE_DISPLAY_FORMAT : undefined}
      width={alternativeFormat ? 75 : undefined}
      transitionDuration={150} // Disable month transition animations in Cypress
    />
  );
};

export default CustomDateRange;
